import { useProfileInfo } from '@/hooks/useProfileId'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import {
  ButtonVariant,
  Card,
  Divider,
  Icon,
  LoadingAnimation,
  Text,
} from '@/components'
import { Clipboard, CreditCard, Globe, Layers, Map, Zap } from 'react-feather'
import React from 'react'
import { Link } from 'app-components'
import { Plan, Utility, UtilityAccount } from '@/types'
import { HomeIcon } from 'lucide-react'
import { utilityAccountsCollection } from '@/reducers/utilityAccounts'
import { match, P } from 'ts-pattern'
import MissingContentSection, {
  MissingContentSectionPlan,
} from '@/app/components/MissingContentSection/MissingContentSection'
import { useNavigation } from '@/app/hooks'
import LabeledData from '@/app/components/LabeledData/LabeledData'
import PendingPlanAlert from 'features/connect-utility/UtilityOnboarding/PendingPlanAlert'
import { useAccountFieldCmsOverrides } from '@/app/features/utility-account-fields/useAccountFieldCmsOverrides'

export default function ProfileUtilityCard() {
  const { utility, plan, home, isLoading: fetchingProfile } = useProfileInfo()
  const { isLoadingOrIdle: fetchingUtility, data: utilityAccountData } =
    utilityAccountsCollection.useFetch('/utilities/utility_account')
  const navigate = useNavigation()
  const isLoading = fetchingUtility || fetchingProfile

  const onNoUtilityClick = () => {
    if (!home) {
      navigate.push('/connect-utility/address')
      return
    }

    navigate.push('/connect-utility/select')
  }

  const loadingContent = (
    <Card className="py-6">
      <div className="flex flex-col items-center">
        <LoadingAnimation type="falling" centered />{' '}
        <Text variant="body1" className="mt-2">
          Loading utility information...
        </Text>
      </div>
    </Card>
  )

  const utilitySection = match(utility)
    .with(P.nullish, () => (
      <MissingContentSection
        title="Add Your Utility Information"
        subtitle="Tell us about your utility provider and plan so we can find programs you
      might be eligible for."
        buttonText="Add Utility Information"
        onClick={onNoUtilityClick}
        iconComponent={<Icon name="MoneyInHand" />}
      />
    ))
    .otherwise((utility) => <UtilitySection utility={utility} />)

  const planSection = match({ utility, plan })
    .with({ utility: P.nullish, plan: P.nullish }, () => null)
    .with({ utility: P.nullish, plan: P.nonNullable }, () => null)
    .with({ utility: P.nonNullable, plan: P.nullish }, () => (
      <>
        <Text variant="subheader" className="mt-2">
          Plan
        </Text>
        <MissingContentSectionPlan />
      </>
    ))
    .with({ utility: P.any, plan: P.nonNullable }, ({ plan }) => (
      <PlanSection plan={plan} />
    ))
    .exhaustive()

  const loadedContent = (
    <Card className="py-6">
      <Text variant="h3">Utility Information</Text>
      {utilitySection}
      {planSection}
      {utility ? (
        <AccountDetailsSection utilityAccount={utilityAccountData[0]} />
      ) : null}
      <Divider />
      {utility ? (
        <div className="flex flex-col items-center mt-4">
          <Link
            className="!text-[14px]"
            onClick={() => navigate.push('/manage-utility-information')}
          >
            Manage
          </Link>
        </div>
      ) : null}
    </Card>
  )

  return (
    <LoadingContainer
      isLoading={isLoading}
      loadedContent={loadedContent}
      loadingContent={loadingContent}
    />
  )
}

function UtilitySection({ utility }: { utility: Utility }) {
  return (
    <>
      <Text variant="subheader" className="mt-2">
        Provider
      </Text>
      <RowContainer>
        <LabeledData
          icon={<Zap size={20} />}
          title="Utility Name"
          subtitle={utility.name}
        />
        <LabeledData
          icon={<Map size={20} />}
          title="Service Area"
          subtitle={utility.place}
        />
        <LabeledData
          icon={<Globe size={20} />}
          title="Utility Website"
          subtitle={utility.website}
          href={utility.website}
        />
      </RowContainer>
    </>
  )
}

function PlanSection({ plan }: { plan: Plan }) {
  const pendingPlanContent = PendingPlanAlert()

  return (
    <>
      <Text variant="subheader" className="mt-2">
        Plan
      </Text>
      {Boolean(pendingPlanContent) && (
        <div className="my-4">{pendingPlanContent}</div>
      )}
      {!pendingPlanContent && (
        <RowContainer>
          <LabeledData
            icon={<Layers size={20} />}
            title="Plan Name"
            subtitle={plan.name}
          />
          <LabeledData
            icon={<CreditCard size={20} />}
            title="Plan Type"
            subtitle={plan.types?.join(', ')}
          />
          <LabeledData
            icon={<HomeIcon size={20} />}
            title="Customer Type"
            subtitle={plan.sector}
          />
          <LabeledData
            icon={<Globe size={20} />}
            title="Plan Details"
            subtitle={plan.source}
            href={plan.source}
          />
        </RowContainer>
      )}
    </>
  )
}

function AccountDetailsSection({
  utilityAccount,
}: {
  utilityAccount: UtilityAccount
}) {
  const navigate = useNavigation()
  const overrides = useAccountFieldCmsOverrides()
  const buttonProps = !utilityAccount
    ? {
        text: 'Add',
        variant: 'primary' as ButtonVariant,
        onClick: () => navigate.push('/connect-utility/account-number'),
      }
    : undefined
  const contractNumber = utilityAccount?.contract_number
  const meterNumber = utilityAccount?.meter_number
  return (
    <>
      <Text variant="subheader" className="mt-2">
        Account Details
      </Text>
      <RowContainer>
        <LabeledData
          icon={<Clipboard size={20} />}
          title={overrides.account_number.label}
          subtitle={utilityAccount?.account_number ?? 'Not Provided'}
          buttonProps={buttonProps}
        />
        {contractNumber && (
          <LabeledData
            icon={<Layers size={20} />}
            title={overrides.contract_number.label}
            subtitle={contractNumber}
          />
        )}
        {meterNumber && (
          <LabeledData
            icon={<Zap size={20} />}
            title={overrides.meter_number.label}
            subtitle={meterNumber}
          />
        )}
      </RowContainer>
    </>
  )
}

function RowContainer({ children }: { children: React.ReactNode }) {
  return <div className="my-4 flex flex-col gap-4">{children}</div>
}
